import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { getChartColors } from "../charts/chartOptions";
import { useTheme } from "../theme/ThemeProvider";
import { formatMoney } from "../../lib/money";

export interface BreakdownBucket {
  readonly label: string;
  readonly trade_count: number;
  readonly win_rate: string | null;
  readonly net_pnl_amount: string;
}

export interface BreakdownTableProps {
  readonly title: string;
  readonly buckets: ReadonlyArray<BreakdownBucket>;
  readonly currency: string;
  readonly testId: string;
}

const PERCENT_FRACTION_DIGITS = 1;

function formatWinRate(value: string | null): string {
  // null = empty bucket (win rate undefined). Guard it explicitly: Number(null)
  // is 0, which would render a misleading "0.0%" instead of a dash.
  if (value === null) {
    return "—";
  }
  const ratio = Number(value);
  if (!Number.isFinite(ratio)) {
    return "—";
  }
  return `${(ratio * 100).toFixed(PERCENT_FRACTION_DIGITS)}%`;
}

function maxAbsNetPnL(buckets: ReadonlyArray<BreakdownBucket>): number {
  let max = 0;
  for (const b of buckets) {
    const abs = Math.abs(Number(b.net_pnl_amount));
    if (Number.isFinite(abs) && abs > max) {
      max = abs;
    }
  }
  return max;
}

export function BreakdownTable({
  title,
  buckets,
  currency,
  testId,
}: BreakdownTableProps) {
  const max = maxAbsNetPnL(buckets);
  // Subscribe to theme so chart colors re-resolve when the user switches themes.
  useTheme();
  const colors = getChartColors();
  return (
    <section data-testid={testId} className="space-y-2">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-on-surface-variant">
        {title}
      </h2>
      {buckets.length === 0 ? (
        <p className="text-sm text-on-surface-variant">No data.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead className="text-right">Trades</TableHead>
              <TableHead className="text-right">Win %</TableHead>
              <TableHead className="text-right">Net PnL</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {buckets.map((bucket) => {
              const amount = Number(bucket.net_pnl_amount);
              const widthPct =
                max > 0 && Number.isFinite(amount)
                  ? (Math.abs(amount) / max) * 100
                  : 0;
              const isNegative = Number.isFinite(amount) && amount < 0;
              const barColor = isNegative ? colors.negative : colors.positive;
              return (
                <TableRow key={bucket.label}>
                  <TableCell>{bucket.label}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {bucket.trade_count}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatWinRate(bucket.win_rate)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <div className="flex items-center justify-end gap-2">
                      <div
                        aria-hidden="true"
                        data-testid={`${testId}-bar`}
                        data-negative={isNegative ? "true" : "false"}
                        data-width-pct={widthPct.toFixed(2)}
                        className="h-2 rounded"
                        style={{
                          width: `${widthPct}%`,
                          maxWidth: "8rem",
                          minWidth: widthPct > 0 ? "0.25rem" : "0",
                          backgroundColor: barColor,
                          opacity: 0.6,
                        }}
                      />
                      <span>
                        {formatMoney(bucket.net_pnl_amount, { currency })}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
